import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Components/Button/Button";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import readerApi from "../../services/readerApi";
import "./Cashier.css";

const truncateTid = (tid, maxLen = 12) => {
  const s = String(tid || "-");
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}...`;
};

const Cashier = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const openTagDetails = (tid) => { if (tid) navigate(`/tags/${encodeURIComponent(tid)}`); };
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [powerSaving, setPowerSaving] = useState(false);
  const [cashierPower, setCashierPower] = useState("30");
  const [lastError, setLastError] = useState("");
  const [snapshot, setSnapshot] = useState({
    items: [],
    unregisteredItems: [],
    total: 0,
    unregisteredCount: 0,
    tagCount: 0,
    paid: false,
  });
  const pollRef = useRef(null);

  const fetchDevices = useCallback(async () => {
    try {
      const data = await readerApi.getDesktopDevices();
      const list = data.devices || [];
      setDevices(list);
      if (!selectedDevice && list.length > 0) {
        setSelectedDevice(list[0].deviceId);
      }
    } catch (err) {
      showToast(err.message || "Failed to load desktop devices", 3000, true, "danger");
    }
  }, [selectedDevice, showToast]);

  const fetchStatus = useCallback(async (deviceId) => {
    if (!deviceId) return;
    try {
      const data = await readerApi.getDesktopPosStatus(deviceId);
      setRunning(!!data.running);
      setLastError(data.lastError || "");
      setSnapshot(data.snapshot || { items: [], unregisteredItems: [], total: 0, unregisteredCount: 0, tagCount: 0, paid: false });
    } catch (err) {
      setRunning(false);
      setLastError(err.message || "Failed to fetch POS status");
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    if (!selectedDevice) return;
    fetchStatus(selectedDevice);
  }, [selectedDevice, fetchStatus]);

  useEffect(() => {
    if (!selectedDevice || !running) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => fetchStatus(selectedDevice), 700);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedDevice, running, fetchStatus]);

  const deviceOptions = useMemo(
    () => [
      { label: "Select Desktop POS Device", value: "" },
      ...devices.map((d) => ({ label: `${d.name} (${d.deviceId})`, value: d.deviceId })),
    ],
    [devices]
  );
  const selectedDeviceDoc = useMemo(
    () => devices.find((d) => d.deviceId === selectedDevice) || null,
    [devices, selectedDevice]
  );

  useEffect(() => {
    const devicePower = selectedDeviceDoc?.power;
    setCashierPower(String(Number.isFinite(Number(devicePower)) ? devicePower : 30));
  }, [selectedDeviceDoc]);

  const handleStart = async () => {
    if (!selectedDevice) {
      showToast("Select a desktop device first", 2500, true, "danger");
      return;
    }
    setLoading(true);
    try {
      await readerApi.startDesktopPos(selectedDevice);
      showToast("Desktop POS started", 2500, true, "success");
      fetchStatus(selectedDevice);
    } catch (err) {
      showToast(err.message || "Failed to start desktop POS", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!selectedDevice) return;
    setLoading(true);
    try {
      await readerApi.stopDesktopPos(selectedDevice);
      showToast("Desktop POS stopped", 2500, true, "success");
      setRunning(false);
      setSnapshot({ items: [], unregisteredItems: [], total: 0, unregisteredCount: 0, tagCount: 0, paid: false });
    } catch (err) {
      showToast(err.message || "Failed to stop desktop POS", 3000, true, "danger");
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!selectedDevice) return;
    setPaying(true);
    try {
      const username = user?.username || user?.id || "";
      const res = await readerApi.payDesktopPos(selectedDevice, username);
      showToast(`${res.message || "PAID"} — removed ${res.deletedTags || 0} tag(s) from DB`, 3500, true, "success");
      await fetchStatus(selectedDevice);
    } catch (err) {
      showToast(err.message || "Payment action failed", 3000, true, "danger");
    } finally {
      setPaying(false);
    }
  };

  const handleApplyPower = async () => {
    if (!selectedDevice) {
      showToast("Select a desktop device first", 2500, true, "danger");
      return;
    }

    const powerVal = Number(cashierPower);
    if (!Number.isFinite(powerVal) || powerVal < 5 || powerVal > 33) {
      showToast("Power must be between 5 and 33 dBm", 3000, true, "danger");
      return;
    }

    setPowerSaving(true);
    try {
      const res = await readerApi.setDesktopPosPower(selectedDevice, powerVal);
      setDevices((prev) => prev.map((d) => (d.deviceId === selectedDevice ? { ...d, power: powerVal } : d)));
      const mode = res.running ? "applied live" : "saved";
      showToast(`Power ${mode}: ${powerVal} dBm`, 2500, true, "success");
      await fetchStatus(selectedDevice);
    } catch (err) {
      showToast(err.message || "Failed to update power", 3000, true, "danger");
    } finally {
      setPowerSaving(false);
    }
  };

  return (
    <div className="operators-page cashier-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Cashier POS</h1>
            <p className="dashboard-subtitle">Admin-only desktop USB cashier workflow</p>
          </div>
          <div className="dfcc gap-2">
            <Button value="Refresh Devices" iconLeft="fa-refresh" variant="outline" onClick={fetchDevices} />
          </div>
        </div>

        <div className="filters-section cashier-controls">
          <div className="filters-grid cashier-controls-grid">
            <Dropdown options={deviceOptions} value={selectedDevice} onChange={setSelectedDevice} placeholder="Select Device" />
            <div className="form-field cashier-power-field">
              <div className="form-label-wrapper">
                <span className="form-label">Desktop Power</span>
                <span className="cashier-power-value">
                  {cashierPower} dBm
                </span>
              </div>
              <div className="cashier-power-row">
                <span className="cashier-power-bound">5</span>
                <input
                  className="cashier-power-slider"
                  type="range"
                  min="5"
                  max="33"
                  step="1"
                  value={cashierPower}
                  onChange={(e) => setCashierPower(e.target.value)}
                  disabled={!selectedDevice || powerSaving}
                />
                <span className="cashier-power-bound">33</span>
              </div>
            </div>
            <Button
              value={powerSaving ? "Saving..." : "Apply Power"}
              iconLeft={powerSaving ? "fa-spinner fa-spin" : "fa-sliders"}
              variant="outline"
              onClick={handleApplyPower}
              disabled={!selectedDevice || powerSaving}
            />
            <Button
              value={loading ? (running ? "Stopping..." : "Starting...") : (running ? "Stop" : "Start")}
              iconLeft={loading ? "fa-spinner fa-spin" : (running ? "fa-stop" : "fa-play")}
              variant={running ? "outline" : "primary"}
              onClick={running ? handleStop : handleStart}
              disabled={loading || !selectedDevice}
            />
            <Button
              value={paying ? "Paying..." : "Pay"}
              iconLeft={paying ? "fa-spinner fa-spin" : "fa-credit-card"}
              variant="primary"
              onClick={handlePay}
              disabled={paying || !running || snapshot.items.length === 0}
            />
          </div>
          <div className="cashier-meta-row">
            <div className="cashier-status-line">
              Status:
              <strong className={`cashier-status-badge ${running ? "running" : "stopped"}`}>
                {running ? "RUNNING" : "STOPPED"}
              </strong>
              {snapshot.paid ? <span className="cashier-paid-badge">PAID</span> : null}
            </div>
            <div className="cashier-updated-at">
              Updated: {snapshot.updatedAt ? new Date(snapshot.updatedAt).toLocaleTimeString() : "-"}
            </div>
          </div>
          {lastError ? <div className="cashier-error-line">Reader Error: {lastError}</div> : null}
        </div>

        <div className="cashier-layout-grid">
          <div className="cashier-main-column">
            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Detected Registered Items</h2>
                <span className="results-count">({snapshot.tagCount} tags)</span>
              </div>
              <div className="table-container">
                {snapshot.items.length === 0 ? (
                  <div className="empty-state">
                    <i className="fa fa-shopping-basket empty-state-icon" />
                    <p>No registered items on reader</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>EPC</th>
                        <th>Tag ID(s)</th>
                        <th>Partition(s)</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Line Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshot.items.map((item) => (
                        <tr key={item.epc}>
                          <td><span className="epc-cell fw-bold">{item.name}</span></td>
                          <td className="epc-cell">{item.epc}</td>
                          <td>
                            {(item.tags || []).length > 0 ? (
                              <div className="cashier-tag-list">
                                {(item.tags || []).map((t, i) => (
                                  <span
                                    key={i}
                                    className="cashier-tag-badge cashier-tag-expandable"
                                    onClick={() => openTagDetails(t.tid)}
                                    role="button"
                                    tabIndex={t.tid ? 0 : -1}
                                    onKeyDown={(e) => { if (e.key === "Enter") openTagDetails(t.tid); }}
                                    title={t.tid ? "Open tag details" : undefined}
                                    style={{ cursor: t.tid ? "pointer" : "default" }}
                                  >
                                    {truncateTid(t.tid)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            {(item.tags || []).length > 0 ? (
                              <div className="cashier-tag-list">
                                {(item.tags || []).map((t, i) => (
                                  <span key={i} className="cashier-room-badge">{t.room || "Unassigned"}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td><span className="tag-badge">{item.quantity}</span></td>
                          <td>${Number(item.price || 0).toFixed(2)}</td>
                          <td><strong>${Number(item.lineTotal || 0).toFixed(2)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="table-section">
              <div className="section-header">
                <h2 className="section-title">Unregistered Tags (Excluded)</h2>
                <span className="results-count">({snapshot.unregisteredCount || 0})</span>
              </div>
              <div className="table-container">
                {(!snapshot.unregisteredItems || snapshot.unregisteredItems.length === 0) ? (
                  <div className="empty-state">
                    <i className="fa fa-tag empty-state-icon" />
                    <p>No unregistered tags</p>
                  </div>
                ) : (
                  <table className="tags-table">
                    <thead>
                      <tr>
                        <th>EPC</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshot.unregisteredItems.map((item) => (
                        <tr key={item.epc}>
                          <td className="epc-cell">{item.epc}</td>
                          <td><span className="tag-badge">{item.quantity}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="table-section cashier-sidebar">
            <div className="section-header">
              <h2 className="section-title">Checkout Sidebar</h2>
            </div>
            <div className="table-container cashier-sidebar-body">
              <div className="cashier-stats-grid">
                <div className="stat-card">
                  <div className="stat-content">
                    <p className="stat-label">Registered Tags</p>
                    <h3 className="stat-value">{snapshot.tagCount}</h3>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-content">
                    <p className="stat-label">Unregistered Tags (excluded)</p>
                    <h3 className="stat-value">{snapshot.unregisteredCount || 0}</h3>
                  </div>
                </div>
                <div className="stat-card cashier-total-card">
                  <div className="stat-content">
                    <p className="stat-label">TOTAL</p>
                    <h3 className="stat-value cashier-total-value">${Number(snapshot.total || 0).toFixed(2)}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cashier;
